import { Box, Typography, useTheme } from "@mui/material";
import { hairline, restingSurface, textSecondary } from "../steps/stepStyles";

/**
 * The showcase step's proof chart: NVIDIA quarterly revenue from their real
 * filings (fixture mirrors the landing site's nvdaRevenue.ts, sourced from FMP
 * income statements). Bars grow in from the axis left-to-right — a
 * self-contained SVG rather than the app's Nivo stack, since this renders a
 * fixed fixture with no interaction.
 */

// Newest last; values in $B. Labels are calendar quarters of the report date.
const QUARTERS: { label: string; revenue: number }[] = [
  { label: "Q3 '24", revenue: 35.1 },
  { label: "Q4 '24", revenue: 39.3 },
  { label: "Q1 '25", revenue: 44.1 },
  { label: "Q2 '25", revenue: 46.7 },
  { label: "Q3 '25", revenue: 57.0 },
  { label: "Q4 '25", revenue: 68.1 },
  { label: "Q1 '26", revenue: 81.6 },
];

const WIDTH = 300;
const HEIGHT = 150;
const CHART_TOP = 22;
const CHART_BOTTOM = 128;
const GAP = 10;

export const ShowcaseChart = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const max = Math.max(...QUARTERS.map((quarter) => quarter.revenue));
  const barWidth = (WIDTH - GAP * (QUARTERS.length + 1)) / QUARTERS.length;
  const labelColor = isDark ? "#e5e7eb" : "#374151";

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          borderRadius: 3,
          border: `1px solid ${hairline(isDark)}`,
          backgroundColor: restingSurface(isDark),
          p: 2,
          pb: 1,
          "@keyframes showcaseBarGrow": {
            from: { transform: "scaleY(0)" },
            to: { transform: "scaleY(1)" },
          },
          "& .showcase-bar": {
            transformBox: "fill-box",
            transformOrigin: "bottom",
            animation: "showcaseBarGrow 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards",
          },
          "@keyframes showcaseLabelIn": { from: { opacity: 0 }, to: { opacity: 1 } },
          "& .showcase-value": { animation: "showcaseLabelIn 0.3s ease 0.8s backwards" },
          "@media (prefers-reduced-motion: reduce)": {
            "& .showcase-bar, & .showcase-value": { animation: "none" },
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 700,
            color: isDark ? "#ffffff" : "#1d1d1f",
            mb: 1,
          }}
        >
          NVIDIA · Quarterly revenue
        </Typography>
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width="100%" role="img" aria-label="NVIDIA quarterly revenue bar chart">
          <g stroke={isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"} strokeWidth="1">
            <line x1="0" y1={CHART_TOP + 26} x2={WIDTH} y2={CHART_TOP + 26} />
            <line x1="0" y1={CHART_TOP + 62} x2={WIDTH} y2={CHART_TOP + 62} />
            <line x1="0" y1={CHART_BOTTOM} x2={WIDTH} y2={CHART_BOTTOM} />
          </g>
          {QUARTERS.map((quarter, index) => {
            const barHeight = ((CHART_BOTTOM - CHART_TOP) * quarter.revenue) / max;
            const x = GAP + index * (barWidth + GAP);
            const isLast = index === QUARTERS.length - 1;
            return (
              <g key={quarter.label}>
                <rect
                  className="showcase-bar"
                  x={x}
                  y={CHART_BOTTOM - barHeight}
                  width={barWidth}
                  height={barHeight}
                  rx="3"
                  fill="#22c55e"
                  opacity={isLast ? 1 : 0.55 + (index / QUARTERS.length) * 0.35}
                  style={{ animationDelay: `${index * 0.07}s` }}
                />
                {isLast && (
                  <text
                    className="showcase-value"
                    x={x + barWidth / 2}
                    y={CHART_BOTTOM - barHeight - 7}
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="700"
                    fill={labelColor}
                  >
                    $81.6B
                  </text>
                )}
                <text
                  x={x + barWidth / 2}
                  y={CHART_BOTTOM + 15}
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="600"
                  fill={textSecondary(isDark)}
                >
                  {quarter.label}
                </text>
              </g>
            );
          })}
        </svg>
      </Box>
      <Typography
        sx={{
          mt: 0.75,
          fontSize: "0.6875rem",
          textAlign: "center",
          color: isDark ? "#6b7280" : "#9ca3af",
        }}
      >
        Real data from company filings, live in the app
      </Typography>
    </Box>
  );
};
